import { type TypeSelectOption } from '@thefarmersfront/kitchen';

/**
 * [반품 교환 관리] 페이지네이션 관련 상수
 */
export const INITIAL_CLAIM_PAGE_SIZE = 50;
export const INITIAL_CLAIM_PAGE_CURSOR = 0;

export const CLAIM_PAGE_SIZE = {
  50: {
    value: 50,
    text: '50개',
  },
  100: {
    value: 100,
    text: '100개',
  },
  200: {
    value: 200,
    text: '200개',
  },
} as const;

export type TypeClaimPageSize = keyof typeof CLAIM_PAGE_SIZE;

/**
 * [반품 & 교환] 키워드 타입
 */
export const KEYWORD_TYPE = {
  CLIENT_ORDER_CODE: 'CLIENT_ORDER_CODE',
  OUTBOUND_ORDER_CODE: 'OUTBOUND_ORDER_CODE',
  RETURN_RECEIPT_ID: 'RETURN_RECEIPT_ID',
  RECEIVER_NAME: 'RECEIVER_NAME',
  EXCHANGE_RECEIPT_ID: 'EXCHANGE_RECEIPT_ID',
} as const;

/**
 * [반품 & 교환] 키워드 선택에 따른 placeholder 문구
 */
export const KEYWORD_PLACE_HOLDER = {
  CLIENT_ORDER_CODE: '복수 입력 시 ","로 구분',
  OUTBOUND_ORDER_CODE: '복수 입력 시 ","로 구분',
  RETURN_RECEIPT_ID: '복수 입력 시 ","로 구분',
  EXCHANGE_RECEIPT_ID: '복수 입력 시 ","로 구분',
  RECEIVER_NAME: '입력',
} as const;

/**
 * [반품 신청/조회] 반품 상태
 */
export const CLAIM_RETURN_STATUS = {
  RETURN_RECEIPT_COMPLETE: { value: 'RETURN_RECEIPT_COMPLETE', text: '회수예정' },
  RETURN_DELIVERY_ING: { value: 'RETURN_DELIVERY_ING', text: '회수중' },
  RETURN_DELIVERY_CANCELED: { value: 'RETURN_DELIVERY_CANCELED', text: '회수취소' },
  RETURN_DELIVERY_FAILED: { value: 'RETURN_DELIVERY_FAILED', text: '회수실패' },
  RETURN_INSPECTION_ING: { value: 'RETURN_INSPECTION_ING', text: '검수중' },
  RETURN_INSPECTION_SUCCESS: { value: 'RETURN_INSPECTION_SUCCESS', text: '검수성공' },
  RETURN_INSPECTION_FAILED: { value: 'RETURN_INSPECTION_FAILED', text: '검수실패' },
  RETURN_INSPECTION_DELAYED: { value: 'RETURN_INSPECTION_DELAYED', text: '검수지연' },
} as const;

export type TypeClaimReturnStatus = keyof typeof CLAIM_RETURN_STATUS;

/**
 * [반품 신청/조회] 반품 상태 - 뱃지 색상 매핑
 */
export const CLAIM_RETURN_STATUS_BADGE_COLOR = {
  RETURN_RECEIPT_COMPLETE: 'secondary',
  RETURN_DELIVERY_ING: 'info',
  RETURN_DELIVERY_CANCELED: 'warning',
  RETURN_DELIVERY_FAILED: 'error',
  RETURN_INSPECTION_ING: 'info',
  RETURN_INSPECTION_SUCCESS: 'success',
  RETURN_INSPECTION_FAILED: 'error',
  RETURN_INSPECTION_DELAYED: 'warning',
} as const;

/**
 * [반품 신청/조회] 반품 회수 송장 상태
 */
export const CLAIM_RETURN_COURIER_STATUS = {
  COURIER_DELIVERED_PROCESSING: { value: 'COURIER_DELIVERED_PROCESSING', text: '회수중' },
  COURIER_DELIVERED_FAIL: { value: 'COURIER_DELIVERED_FAIL', text: '회수실패' },
  COURIER_DELIVERED_CANCELED: { value: 'COURIER_DELIVERED_CANCELED', text: '회수취소' },
  COURIER_DELIVERED_COMPLETE: { value: 'COURIER_DELIVERED_COMPLETE', text: '회수완료' },
} as const;

export type TypeClaimReturnCourierStatus = keyof typeof CLAIM_RETURN_COURIER_STATUS;

/**
 * [반품 신청/조회] 반품 회수 송장 상태 - 뱃지 색상 매핑
 */
export const CLAIM_RETURN_COURIER_STATUS_BADGE_COLOR = {
  COURIER_DELIVERED_PROCESSING: 'info',
  COURIER_DELIVERED_FAIL: 'error',
  COURIER_DELIVERED_CANCELED: 'warning',
  COURIER_DELIVERED_COMPLETE: 'success',
} as const;

/**
 * [반품 신청/조회] 반품 신청 상세 > 입하 상태 - 뱃지 색상 매핑
 */
export const CLAIM_RETURN_INBOUND_STATUS_BADGE_COLOR = {
  입하전: 'secondary',
  입하완료: 'success',
  입하불가: 'error',
} as const;

/**
 * [반품 신청/조회] 반품 신청 상세 > 검수 상태 - 뱃지 색상 매핑
 */
export const CLAIM_RETURN_INSPECTION_STATUS_BADGE_COLOR = {
  검수전: 'secondary',
  검수성공: 'success',
  검수실패: 'error',
} as const;

/**
 * [반품 신청/조회] 회수 배송사 고정값
 */
export const DEFAULT_RETURN_COURIER_NAME = '롯데';

/**
 * [교환 신청/조회] 교환 유형
 */
export const CLAIM_EXCHANGE_TYPE = {
  NORMAL: { value: 'NORMAL', text: '일반교환' },
  PRE_EXCHANGE: { value: 'PRE_EXCHANGE', text: '선교환' },
} as const;

export type TypeClaimExchangeType = keyof typeof CLAIM_EXCHANGE_TYPE;

/**
 * [반품 교환 관리] 키워드 검색 최대 검색 개수
 */
export const MAX_CLAIM_KEYWORD_COUNT = 10;

/**
 * [반품 교환 관리] 날짜 검색 최대 기간
 */
export const MAX_CLAIM_DATE_RANGE = 31;

/**
 * [반품 교환 관리] 박스 총 수량 최소, 최대 값
 */

export const CLAIM_TOTAL_BOX_COUNT = {
  MIN: 1,
  MAX: 99,
} as const;

/**
 * [반품 교환 관리]'박스 총 수량' 선택 옵션
 */
export const CLAIM_TOTAL_BOX_OPTIONS: TypeSelectOption[] = [
  ...Array.from({ length: 10 }, (_, index) => ({ value: index + 1, text: `${index + 1}개` })),
  { value: 'ETC', text: '직접입력' },
];
